
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { TProvideDataSelectAnswerType } from "@/modules/Questionnaire/components/AnswerBlocks/TAnswerBlocks";
import { SMQuestionnaire, SMTasks } from "@/store";

import ProvidedDataAddNewValueForm from "@/modules/Questionnaire/components/AnswerBlocks/components/ProvidedDataSelect/ProvidedDataAddNewValueForm.vue";
import { API_ROUTES } from "@/utils/API";
import DeleteAddressPopUp from "@/components/Popups/DeleteAddressPopUp.vue";

const ANSWER_BLOCK_TYPE = "provided-data-select";

const RADIO_ANSWER_SUB_TYPE = "radio";

const MODEL_CLASS = "provided-data";
const OPTION_CLASS = "provided-data-item";
const ADDITIONAL_CONTROLS_CLASS = "additional-controls";

const ADDRESS_QUESTION_TYPE = "address";
const PAYE_QUESTION_TYPE = "paye";
const DEPENDANTS_QUESTION_TYPE = "dependants";

export type ProvideDataOption = {
  title?: string;
  label?: string;
  value?: any;
  selection?: boolean | undefined;
};

const ICONS = {
  close: "close-outline",
};

const BRANCH_ADDRESS_TYPE = "branch";
const TRADING_ADDRESS_ADDRESS_TYPE = "trading_address";

const DEFAULT_API_ROUTE_TO_REMOVE_ITEM = API_ROUTES.DELETE_ADDRESS_BRANCH;

const REMOVE_ITEM_SUCCESS_MESSAGE = "success";

@Component({
  components: {
    DeleteAddressPopUp,
    ProvidedDataAddNewValueForm,
  },
})
export default class AnswerBlockProvideDataSelect extends Vue {
  @Prop({ type: Object, default: () => ({}) })
  readonly answerBlockData!: TProvideDataSelectAnswerType;
  @Prop({ type: Number, default: () => 0 }) readonly index: number | undefined;
  @Prop({ type: Number, default: () => 0 }) readonly loopIndex: number | undefined;
  @Prop({ type: String, default: DEFAULT_API_ROUTE_TO_REMOVE_ITEM })
  readonly apiRouteToRemoveItem?: string;

  ICONS = ICONS;

  input?: string | Array<any> = "";

  editing = false;

  @Watch("editing")
  updateEditing(value: boolean) {
    SMQuestionnaire.setAnswerBlockEditing(value);

    this.$forceUpdate();
  }

  @Watch("input")
  updateAnswer(value: string) {
    const { index, answerBlockData, loopIndex } = this;

    if (index == null) return;

    SMQuestionnaire.updateAnswer({
      index,
      value,
      required: answerBlockData.is_required,
      answer_block_id: answerBlockData.ID,
      loop_index: loopIndex,
      condition: answerBlockData.condition,
    });
  }

  getAnswerBlockData() {
    const answer = SMQuestionnaire.answers.find(
      (answer) =>
        answer.answer_block_id === this.answerBlockData.ID && this.loopIndex === answer.loop_index
    );

    if (!answer) return;

    let { value } = answer;

    value = typeof value === "string" ? value?.split(",,") ?? [] : [];

    return this.index != null && answer && value ? value : [];
  }

  get questionType() {
    return this.answerBlockData?.question_type ?? "";
  }

  get isAddressQuestionType() {
    return this.questionType === ADDRESS_QUESTION_TYPE;
  }

  get isDependantsQuestionType() {
    return this.questionType === DEPENDANTS_QUESTION_TYPE;
  }

  get isPayeQuestionType() {
    return this.questionType === PAYE_QUESTION_TYPE;
  }

  get showTitle() {
    return this.isAddressQuestionType;
  }

  get textSeparatorLabel() {
    return this.$tc("labels.or");
  }

  get showNoneOfAbove() {
    return !!this.answerBlockData?.none_of_the_above && !this.isPayeQuestionType;
  }

  get noneOfAboveLabel() {
    return this.isDependantsQuestionType
      ? this.$tc("labels.noneOfThese")
      : this.$tc("labels.noneOfTheAbove");
  }

  noneOfTheAbove = false;

  @Watch("noneOfTheAbove")
  updateNoneOfTheAbove(value) {
    if (!value) {
      if (this.options.every((option) => !option.selection)) return (this.input = "");

      return;
    }

    this.selection = [];

    this.options.map((option) => {
      option.selection = false;

      return option;
    });

    this.input = [];
  }

  @Watch("selection")
  updateSelection(arr) {
    if (arr.length) this.noneOfTheAbove = false;
  }

  init = false;
  selection = [];
  options: ProvideDataOption[] = [];

  get payeOptions() {
    return this.options.map((option) => {
      if (option.label || option.title) {
        let label = "";

        if (option.label) {
          label += `<span style="color: #0C2A5D">${option.label}</span>&nbsp;`;
        }

        if (option.title) {
          label += `<span style="color: #536B94">${option.title}</span>`;
        }

        option.label = label;
      }

      return option;
    });
  }

  get payeSelectDisabled() {
    return !this.payeOptions.length;
  }

  get classes() {
    const { question_type } = this.answerBlockData;

    return {
      [`${MODEL_CLASS}_${question_type}-template`]: !!question_type,
    };
  }

  checkIfDeleteEnable(option: ProvideDataOption) {
    return option.value?.can_be_deleted;
  }

  getOptionClasses(option: ProvideDataOption) {
    return {
      [`${OPTION_CLASS}_selected`]: this.selection.includes(option.value) || option.selection,
    };
  }

  get additionalControlsClasses() {
    const { question_type } = this.answerBlockData;

    return {
      [`${ADDITIONAL_CONTROLS_CLASS}_${question_type}-template`]: !!question_type,
    };
  }

  get isInformationBlock() {
    // TODO: DEV-8305. Check with "answerBlock" data;
    return this.answerBlockData.use_as_info_block;
  }

  get showControls() {
    // TODO: DEV-8305. Check with "answerBlock" data;
    return !this.isInformationBlock && !this.isAddressQuestionType;
  }

  get useRadioControls() {
    // TODO: DEV-8305. Check with "answerBlock" data;
    return this.answerBlockData.sub_answer_type === RADIO_ANSWER_SUB_TYPE;
  }

  get addAnotherValueEnable() {
    return this.answerBlockData.show_add_address_button;
  }

  get addressTypeValues() {
    return this.answerBlockData.address_types_list ?? [];
  }

  get displayAddAnotherValue() {
    return !this.showAddAnotherValue && this.addAnotherValueEnable;
  }

  loading = false;

  setOptions() {
    if (!SMQuestionnaire.currentStep) return;

    const { question } = SMQuestionnaire.currentStep;
    const { answer_blocks } = question;

    answer_blocks.forEach((answerBlock) => {
      if (answerBlock.type === ANSWER_BLOCK_TYPE) {
        this.options = (answerBlock.options as unknown) as ProvideDataOption[];
      }
    });
  }

  convertValue(value): string {
    if (!value) return "";

    if (typeof value === "object" && Object.keys(value).length) {
      value = JSON.stringify(value);
    }

    return value;
  }

  prepareOptions(values) {
    this.options.map((option) => {
      const val = this.convertValue(option?.value);

      option["selection"] = val ? values.includes(val) : false;

      return option;
    });

    this.$forceUpdate();
  }

  showAddAnotherValue = false;

  addAnotherValue() {
    this.editing = true;
    this.showAddAnotherValue = true;
  }

  get addAnotherValueButtonLabel() {
    const { questionType } = this;

    return questionType ? this.$tc(`buttons.add.${questionType}`) : this.$tc("buttons.add.default");
  }

  onAnotherValueAdded() {
    this.updateAnswerBlockData();

    this.editing = false;

    this.showAddAnotherValue = false;
  }

  reloadTaskUserFlow() {
    const userFlowId = SMQuestionnaire.userflowID ?? SMTasks.taskUserflow;

    SMQuestionnaire.getCurrentStep(userFlowId);
  }

  updateAnswerBlockData() {
    this.reloadTaskUserFlow();

    this.$forceUpdate();

    setTimeout(() => {
      this.setOptions();
    }, 2000);
  }

  cancelAddAnotherValue() {
    this.editing = false;
    this.showAddAnotherValue = false;

    if (this.isPayeQuestionType) {
      const value = JSON.parse(JSON.stringify(this.input));
      this.input = "";

      this.$nextTick(() => {
        this.input = value;
      });

      this.$forceUpdate();
    }
  }

  actionType = "";
  showDeletePopup = false;
  deletePopupSrc = null;

  get deletePopUpProps() {
    const { actionType, showDeletePopup, deletePopupSrc } = this;

    return {
      show: showDeletePopup,
      action: actionType,
      src: deletePopupSrc,
    };
  }

  deleteCancel() {
    this.showDeletePopup = false;
    this.deletePopupSrc = null;
  }

  async deleteSuccess() {
    await this.removeItemByIndex(this.deletePopupSrc);

    this.deleteCancel();
  }

  async confirmItemRemove(optionIndex, option) {
    this.showDeletePopup = true;

    this.deletePopupSrc = {
      index: optionIndex,
      model: option,
    };
  }

  async removeItemByIndex(payload) {
    if (!payload) return;

    const { index, model } = payload;

    if (!model?.value) return;

    const addressType = model.value?.is_branch ? BRANCH_ADDRESS_TYPE : TRADING_ADDRESS_ADDRESS_TYPE;

    if (!index && index !== 0) return;

    const config = {
      params: {
        address_type: addressType,
        loop_index: model.value?.loop_index,
      },
    };

    const userFlowID = SMQuestionnaire.userflowID ?? SMTasks.taskUserflow;

    if (userFlowID) {
      Object.assign(config.params, {
        user_flow_id: userFlowID,
      });
    }

    const { data } = await this.$http.delete(this.apiRouteToRemoveItem, config);

    if (!data || data?.message !== REMOVE_ITEM_SUCCESS_MESSAGE) return;

    await this.updateAnswerBlockData();
  }

  convertSelection(collection) {
    if (!Array.isArray(collection)) return collection;

    return collection.reduce((acc, cur) => {
      acc.push(this.convertValue(cur));

      return acc;
    }, []);
  }

  async select(option: ProvideDataOption) {
    if (!this.useRadioControls) {
      option.selection = !option.selection;

      this.selection = this.options.reduce((acc, cur) => {
        if (cur.selection) acc.push(cur.value);

        return acc;
      }, []);
    } else {
      const hasSelection = option.selection === true;

      this.options.map((item) => {
        item.selection = false;
      });

      await this.$nextTick();

      if (hasSelection) {
        option.selection = false;

        this.selection = [];
      } else {
        option.selection = true;

        const value = this.convertValue(option.value);

        this.selection = [value];
      }
    }

    this.setValue();

    this.$forceUpdate();
  }

  setValue() {
    this.input = this.convertSelection(this.selection).join(",,");
  }

  async setInitialSelectionAndUpdateValue() {
    this.selection = this.options.reduce((acc, cur) => {
      if (cur.selection) acc.push(cur.value);

      return acc;
    }, []);

    await this.$nextTick();

    this.setValue();
  }

  async mounted() {
    this.setOptions();

    const value = this.getAnswerBlockData();

    this.prepareOptions(value);

    await this.$nextTick();

    await this.setInitialSelectionAndUpdateValue();

    this.init = true;
  }
}
